import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { day } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Empty, Field, LoadMore, Pill, usePagedList } from '../components/ui.jsx';
import { inr } from '../lib/format.js';
import { parseNumber } from '../lib/numbers.js';
import PeriodPicker, { useUrlPeriod } from '../components/PeriodPicker.jsx';
import ExportButton, { fetchAllPages, periodSlug, periodText } from '../components/ExportButton.jsx';
import { useEntityQuery } from '../lib/entity.js';

// Despatches awaiting an invoice or still in progress list every date unless a period is picked.
const VIEWS = [
  { key: 'confirmed', label: 'Awaiting invoice', pending: true },
  { key: 'draft', label: 'In progress', pending: true },
  { key: 'invoiced', label: 'Invoiced' },
  { key: '', label: 'All' },
];

export default function Deliveries() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const pending = VIEWS.some((v) => v.pending && v.key === status);
  const entityQuery = useEntityQuery();
  const { period, setPeriod, query } = useUrlPeriod({ allByDefault: pending });
  const list = usePagedList(`/api/deliveries?${[status && `status=${status}`, query, entityQuery].filter(Boolean).join('&')}`, 'deliveries');
  // Raising an invoice is the counter's and accounts' job, not the godown's.
  const canBill = ['tenant_admin', 'sales_counter', 'accounts'].includes(useSession().user.role);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState(null);
  const navigate = useNavigate();

  const bill = async (deliveryId, e, extras = {}) => {
    if (e) e.stopPropagation();
    setBusy(deliveryId); setError(null);
    try {
      const invoice = await api.post('/api/invoices', { godown_delivery_id: deliveryId, issue: true, ...extras });
      navigate(`/invoices/${invoice.id}`);
    } catch (err) {
      setError(err.details && err.details.batches
        ? `${err.message}: ${err.details.batches.map((b) => `${b.item} / ${b.batch}`).join(', ')}. Record them under Batches & specs.`
        : err.message);
    } finally { setBusy(null); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Despatches</h1>
          <div className="page-sub">
            Document 2 of the sale — what physically left the godown, batch by batch. Billing is one click from here;
            nothing about the goods is keyed again.
          </div>
        </div>
        <div className="inline">
          <div className="segmented" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button key={v.label} aria-pressed={status === v.key}
                onClick={() => setParams(v.key ? { status: v.key } : {})}>{v.label}</button>
            ))}
          </div>
          <PeriodPicker allowAll value={period} onChange={setPeriod} />
          <ExportButton build={async () => ({
            filename: `despatches-${periodSlug(period)}`,
            title: 'Despatches',
            subtitle: `${(VIEWS.find((v) => v.key === status) || { label: 'All' }).label} · ${periodText(period)}`,
            sheets: [{
              name: 'Despatches',
              columns: [
                { key: 'gd_number', header: 'Despatch' }, { key: 'gd_date', header: 'Date', type: 'date' },
                { key: 'do_number', header: 'Order' }, { key: 'buyer_name', header: 'Buyer' },
                { key: 'godown_name', header: 'Godown' }, { key: 'vehicle_no', header: 'Vehicle' },
                { key: 'status', header: 'Status' }, { key: 'invoice_number', header: 'Invoice' },
                { key: 'tally_sync_status', header: 'Tally' },
              ],
              rows: await fetchAllPages(
                `/api/deliveries?${[status && `status=${status}`, query, entityQuery].filter(Boolean).join('&')}`,
                'deliveries'),
            }],
          })} />
        </div>
      </div>

      <Alert onRetry={() => setError(null)}>{error}</Alert>
      <Alert onRetry={list.reload}>{list.error}</Alert>
      {list.loading && !list.data && <CardSkeleton lines={5} />}

      {billing && (
        <BillingOptions deliveryId={billing} busy={busy === billing}
          onCancel={() => setBilling(null)}
          onBill={(extras) => bill(billing, null, extras)} />
      )}

      {list.data && (
        <Card pad={list.data.deliveries.length === 0}>
          {list.data.deliveries.length === 0 ? (
            <Empty title="No despatches in this view">
              Confirmed despatches appear here, ready to bill.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead>
                  <tr><th>Despatch</th><th>Order</th><th>Buyer</th><th>Godown</th>
                    <th>Status</th><th>Invoice</th><th>Tally</th><th /></tr>
                </thead>
                <tbody>
                  {list.data.deliveries.map((d) => (
                    <tr key={d.id} className="clickable"
                      onClick={() => (d.invoice_id ? navigate(`/invoices/${d.invoice_id}`) : navigate('/godown'))}>
                      <td className="mono">{d.gd_number || 'draft'}<div className="sub">{day(d.gd_date)}</div></td>
                      <td className="mono">{d.do_number}</td>
                      <td>{d.buyer_name}</td>
                      <td>{d.godown_name}{d.vehicle_no && <div className="sub">{d.vehicle_no}</div>}</td>
                      <td><Pill value={d.status} /></td>
                      <td className="mono">{d.invoice_number || '—'}</td>
                      <td>{d.tally_sync_status ? <Pill value={d.tally_sync_status} /> : <span className="muted">—</span>}</td>
                      <td className="r">
                        <button className="btn small ghost" title="Delivery challan and packing list"
                          onClick={(e) => {
                            e.stopPropagation(); setError(null);
                            api.open(`/api/deliveries/${d.id}/challan?format=pdf`).catch((err) => setError(err.message));
                          }}>
                          Challan
                        </button>
                        {canBill && d.status === 'confirmed' && !d.invoice_id && (
                          <>
                            <button className="btn small" disabled={busy === d.id} onClick={(e) => bill(d.id, e)}>
                              {busy === d.id ? 'Billing…' : 'Raise invoice'}
                            </button>
                            <button className="btn small ghost" title="Add freight, insurance or an export declaration"
                              onClick={(e) => { e.stopPropagation(); setBilling(billing === d.id ? null : d.id); }}>
                              ⋯
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <LoadMore list={list} />
        </Card>
      )}
    </>
  );
}

const CHARGE_KINDS = [['freight', 'Freight'], ['insurance', 'Insurance'], ['packing', 'Packing'],
  ['loading', 'Loading'], ['other', 'Other']];

/** Charges and the supply type, for the invoices that are not a plain domestic sale. */
function BillingOptions({ deliveryId, busy, onCancel, onBill }) {
  const [supplyType, setSupplyType] = useState('');
  const [reverseCharge, setReverseCharge] = useState(false);
  const [charges, setCharges] = useState([{ kind: 'freight', label: 'Freight', amount: '', gst_rate: 18 }]);
  const [exportInfo, setExportInfo] = useState({ port_code: '', shipping_bill_no: '', shipping_bill_date: '' });

  const [error, setError] = useState(null);
  const total = charges.reduce((a, c) => a + (Number.isFinite(parseNumber(c.amount)) ? parseNumber(c.amount) : 0), 0);

  // A charge typed as "1,200" used to read as NaN and drop out of the invoice
  // without a word. Blank rows are skipped; anything unreadable stops the bill.
  const submit = () => {
    const out = [];
    for (const c of charges) {
      const amount = parseNumber(c.amount);
      if (amount === null || amount === 0) continue;
      const rate = parseNumber(c.gst_rate);
      if (!Number.isFinite(amount) || amount < 0) { setError(`${c.label || c.kind}: the amount must be a number.`); return; }
      if (!Number.isFinite(rate) || rate < 0) { setError(`${c.label || c.kind}: the GST rate must be a number.`); return; }
      out.push({ kind: c.kind, label: c.label, amount, gst_rate: rate });
    }
    setError(null);
    onBill({
      ...(supplyType ? { supply_type: supplyType } : {}),
      ...(reverseCharge ? { reverse_charge: true } : {}),
      ...(isExport ? {
        port_code: exportInfo.port_code || undefined,
        shipping_bill_no: exportInfo.shipping_bill_no || undefined,
        shipping_bill_date: exportInfo.shipping_bill_date || undefined,
      } : {}),
      charges: out,
    });
  };
  const isExport = supplyType.startsWith('EXP') || supplyType.startsWith('SEZ');

  return (
    <Card title="Before billing" note="Charges recovered here join the taxable value of the supply."
      actions={<button className="btn small ghost" onClick={onCancel}>Close</button>}>
      <div className="row">
        <Field label="Supply type" hint="leave as it is for a domestic sale">
          <select value={supplyType} onChange={(e) => setSupplyType(e.target.value)}>
            <option value="">Domestic (decided by the buyer's registration)</option>
            <option value="EXPWP">Export with payment of IGST</option>
            <option value="EXPWOP">Export under LUT, without payment</option>
            <option value="SEZWP">SEZ with payment</option>
            <option value="SEZWOP">SEZ under LUT</option>
            <option value="DEXP">Deemed export</option>
          </select>
        </Field>
        <Field label="Reverse charge" hint="only for goods notified under it">
          <select value={String(reverseCharge)} onChange={(e) => setReverseCharge(e.target.value === 'true')}>
            <option value="false">No — we charge and pay the tax</option>
            <option value="true">Yes — the buyer pays the tax</option>
          </select>
        </Field>
        {isExport && (
          <>
            <Field label="Port code"><input value={exportInfo.port_code}
              onChange={(e) => setExportInfo({ ...exportInfo, port_code: e.target.value })} /></Field>
            <Field label="Shipping bill"><input value={exportInfo.shipping_bill_no}
              onChange={(e) => setExportInfo({ ...exportInfo, shipping_bill_no: e.target.value })} /></Field>
            <Field label="Shipping bill date"><input type="date" value={exportInfo.shipping_bill_date}
              onChange={(e) => setExportInfo({ ...exportInfo, shipping_bill_date: e.target.value })} /></Field>
          </>
        )}
      </div>

      <Alert>{error}</Alert>
      <div className="table-wrap">
        <table>
          <thead><tr><th style={{ width: 150 }}>Charge</th><th>Label on the invoice</th>
            <th className="r" style={{ width: 140 }}>Amount</th><th className="r" style={{ width: 110 }}>GST %</th>
            <th style={{ width: 52 }} /></tr></thead>
          <tbody>
            {charges.map((c, idx) => (
              <tr key={idx}>
                <td>
                  <select value={c.kind}
                    onChange={(e) => setCharges(charges.map((x, i) => (i === idx
                      ? { ...x, kind: e.target.value, label: CHARGE_KINDS.find(([k]) => k === e.target.value)[1] } : x)))}>
                    {CHARGE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </td>
                <td><input value={c.label}
                  onChange={(e) => setCharges(charges.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))} /></td>
                <td className="r"><input className="num" style={{ textAlign: 'right' }} value={c.amount}
                  onChange={(e) => setCharges(charges.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x)))} /></td>
                <td className="r"><input className="num" style={{ textAlign: 'right' }} value={c.gst_rate}
                  onChange={(e) => setCharges(charges.map((x, i) => (i === idx ? { ...x, gst_rate: e.target.value } : x)))} /></td>
                <td className="r"><button className="btn small ghost"
                  onClick={() => setCharges(charges.filter((_, i) => i !== idx))}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="inline" style={{ marginTop: 12 }}>
        <button className="btn secondary small"
          onClick={() => setCharges([...charges, { kind: 'other', label: 'Other charges', amount: '', gst_rate: 18 }])}>
          ＋ Add a charge
        </button>
        <span className="card-note">Charges total <b className="num" style={{ color: 'var(--ink)' }}>{inr(total, 0)}</b></span>
        <button className="btn" disabled={busy} style={{ marginLeft: 'auto' }} onClick={submit}>
          {busy ? 'Billing…' : 'Raise invoice with these'}
        </button>
      </div>
    </Card>
  );
}
